import { createSlice } from "@reduxjs/toolkit";
import { Site } from "@src/@types/SitesTypes";
import { Account, PlanType, Subscription, UserAccount } from "@src/@types/UserAccount";



const INITIAL_STARTE: UserAccount = {
    email: "",
    accounts: []
}
const INITIAL_STATE_SUBSCRIPTION:Subscription = {
    planCode: "0",
    planName: "Trial",
    planType: PlanType.FREE,
    planDescription: "",
    statusLabel: "",
    active: true,
    status: "TRIAL",
    daysUntilRenewal: 0,
    expirationDate: ""
}

const LS_KEY_ACTIVE_ACCOUNT_ID = "activeAccountId";
const LS_KEY_ACTIVE_DOMAIN = "activeDomain";


export function authUserAccount(state = INITIAL_STARTE, action: { type: string; userAccount: UserAccount; }) {
    if (action.type === 'UPDATE_USER') {
        state = action.userAccount
    }
    return state;
}

const restoreUser = (): UserAccount => {
    const userAccount = window.localStorage.getItem("userAccount");
    return userAccount ? JSON.parse(userAccount) : INITIAL_STARTE;
}

const restoreIsToSaveToStorage = (): boolean => {
    const savetoStorageUserAccount = window.localStorage.getItem("saveToStorageUserAccount");
    return savetoStorageUserAccount ? JSON.parse(savetoStorageUserAccount) : false;
}

const restoreActiveAccountId = (): number | null => {
    const raw = window.localStorage.getItem(LS_KEY_ACTIVE_ACCOUNT_ID);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
}

const restoreActiveDomain = (): string | null => {
    return window.localStorage.getItem(LS_KEY_ACTIVE_DOMAIN);
}

const persistActiveAccountId = (id: number | null | undefined) => {
    if (id === null || id === undefined) {
        window.localStorage.removeItem(LS_KEY_ACTIVE_ACCOUNT_ID);
    } else {
        window.localStorage.setItem(LS_KEY_ACTIVE_ACCOUNT_ID, String(id));
    }
}

const persistActiveDomain = (domain: string | null | undefined) => {
    if (domain === null || domain === undefined) {
        window.localStorage.removeItem(LS_KEY_ACTIVE_DOMAIN);
    } else {
        window.localStorage.setItem(LS_KEY_ACTIVE_DOMAIN, domain);
    }
}

// Escolhe a account ativa: respeita o id salvo em localStorage; senão pega a primeira.
const resolveActiveAccount = (userAccount: UserAccount | undefined): Account | undefined => {
    const accounts = userAccount?.accounts;
    if (!accounts || accounts.length === 0) return undefined;
    const savedId = restoreActiveAccountId();
    if (savedId !== null) {
        const found = accounts.find(a => a.astvId === savedId);
        if (found) return found;
    }
    return accounts[0];
}

const getSitesFromAccount = (account: Account | undefined): Site[] => {
    if (!account?.licenses) return [];
    return account.licenses.map(s => ({ domain: s.domain, enabled: s.enabled }));
}

const getSubscriptionFromAccount = (account: Account | undefined): Subscription => {
    return account?.subscription || INITIAL_STATE_SUBSCRIPTION;
}

// Escolhe o domínio ativo dentro da account: se o candidato ainda pertence, mantém;
// senão pega o primeiro; senão null.
const resolveActiveDomain = (
    account: Account | undefined,
    previousDomain: string | null | undefined
): string | null => {
    const licenses = account?.licenses;
    if (!licenses || licenses.length === 0) return null;
    if (previousDomain && licenses.some(l => l.domain === previousDomain)) {
        return previousDomain;
    }
    return licenses[0].domain || null;
}


const initialUserAccount = restoreUser();
const initialActiveAccount = resolveActiveAccount(initialUserAccount);
const initialActiveDomain = resolveActiveDomain(initialActiveAccount, restoreActiveDomain());

export const userAccountSlice = createSlice({
    name: "userAccount",
    initialState: {
        userAccount: initialUserAccount,
        saveToStorage: restoreIsToSaveToStorage(),
        activeAccount: initialActiveAccount,
        activeDomain: initialActiveDomain,
        sites: getSitesFromAccount(initialActiveAccount),
        subscription: getSubscriptionFromAccount(initialActiveAccount),
        isOnLoading: false
    },
    reducers: {
        handleUserAccount: (state, action) => {
            const userAccount: UserAccount = action.payload;
            state.userAccount = userAccount;
            window.localStorage.setItem("userAccount", JSON.stringify(userAccount));

            const nextActive = resolveActiveAccount(userAccount);
            state.activeAccount = nextActive;
            persistActiveAccountId(nextActive?.astvId ?? null);

            state.sites = getSitesFromAccount(nextActive);
            state.subscription = getSubscriptionFromAccount(nextActive);

            const nextDomain = resolveActiveDomain(nextActive, state.activeDomain);
            state.activeDomain = nextDomain;
            persistActiveDomain(nextDomain);
        },
        handleSaveToStorage: (state, action) => {
            state.saveToStorage = action.payload;
            window.localStorage.setItem("saveToStorageUserAccount", JSON.stringify(action.payload));
        },
        handleRemoveAccount: () => {
            console.log("Cleaning userAccount from redux");
            window.localStorage.removeItem("userAccount");
            window.localStorage.removeItem(LS_KEY_ACTIVE_ACCOUNT_ID);
            window.localStorage.removeItem(LS_KEY_ACTIVE_DOMAIN);
        },
        handleSetActiveAccount: (state, action) => {
            const nextAccount: Account | undefined = action.payload;
            console.log("[Redux] handleSetActiveAccount", nextAccount);
            state.activeAccount = nextAccount;
            persistActiveAccountId(nextAccount?.astvId ?? null);
            state.sites = (nextAccount?.licenses ?? []).map((s: Site) => ({
                domain: s.domain,
                enabled: s.enabled
            }));
            if (nextAccount?.subscription) {
                state.subscription = nextAccount.subscription;
            }
            const nextDomain = resolveActiveDomain(nextAccount, state.activeDomain);
            state.activeDomain = nextDomain;
            persistActiveDomain(nextDomain);
        },
        handleSetActiveDomain: (state, action) => {
            const domain: string | null = action.payload ?? null;
            state.activeDomain = domain;
            persistActiveDomain(domain);
        },
        handleSetOnLoading: (state, action) => {
            state.isOnLoading = action.payload;
        }
    }
})
export const {
    handleUserAccount,
    handleSaveToStorage,
    handleRemoveAccount,
    handleSetActiveAccount,
    handleSetActiveDomain,
    handleSetOnLoading
} = userAccountSlice.actions;

export default userAccountSlice.reducer;
